import type { BoardThemes } from '@exports/ChessBoard';
import { IColoredArrow, IColoredSquare, IPgnMove, IToken, Pgn } from '@libs/chess/pgn';

export type IBookDto = {
  id: string;
  title: string;
  chapters: ITocItemDto[];
};
export type ITocItemDto = {
  id: string;
  number: number;
  title: string;
  dirty?: boolean;
  // Конфликт с сервером: правки главы не сохранены — красная метка в списке
  // и пункты разрешения в меню (kind из IChapterConflict)
  conflict?: 'updated' | 'deleted';
  // Собственное имя родительской главы (без ✦); undefined — корневая
  parent?: string;
  // Глава лежит слайдом в уроке: какой версией. undefined — не используется
  usedInLesson?: ILessonUsage;
};

// Как уроки используют главу: актуальной версией или устаревшей
export type ILessonUsage = 'recent' | 'outdated';

export const CHAPTER_TYPES = ['Analysis', 'Test', 'Game Setup', 'Computer', 'Media'] as const;
export const TEST_SCORING_MODES = ['Move Score', 'Attempts Count', 'Moves Count'] as const;
export const CHESS_VARIANTS = ['Standard', '2vs2', 'Seance'] as const;
export const CHAPTER_LEVELS = [
  '0-200',
  '200-600',
  '600-1000',
  '1000-1300',
  '1300-1600',
  '1600-1900',
  '1900-2200',
  '2200+',
] as const;
export const MEDIA_TYPES = ['Image', 'Video'] as const;

export type IChapterType = (typeof CHAPTER_TYPES)[number];
export type IChessVariant = (typeof CHESS_VARIANTS)[number];
export type ITestScoringMode = (typeof TEST_SCORING_MODES)[number];
export type IChapterLevel = (typeof CHAPTER_LEVELS)[number];

export type IMediaType = (typeof MEDIA_TYPES)[number];

export const CHAPTER_TYPE_LABELS: Record<IChapterType, string> = {
  Analysis: 'Позиция',
  Test: 'Задача',
  'Game Setup': 'Игра',
  Computer: 'Компьютер',
  Media: 'Медиа',
};
export const CHAPTER_TYPE_BOARD_THEMES: Record<IChapterType, BoardThemes> = {
  Analysis: 'green',
  Test: 'classic-lite',
  'Game Setup': 'gray-wood',
  Computer: 'gray-wood',
  Media: 'gray-wood',
};
export const TEST_SCORING_MODE_LABELS: Record<ITestScoringMode, string> = {
  'Move Score': 'Лучший ход',
  'Attempts Count': 'Три попытки',
  'Moves Count': 'Минимум ходов',
};
// Пресеты времени на решение задачи (секунды) + вариант «Другое» (своё значение).
export const TEST_TIME_PRESETS: { label: string; seconds: number }[] = [
  { label: '10с', seconds: 10 },
  { label: '30с', seconds: 30 },
  { label: '60с', seconds: 60 },
  { label: '90с', seconds: 90 },
];
// Пресеты контроля времени (игра/компьютер): основное время + добавление (секунды).
export const TIME_CONTROL_PRESETS: {
  label: string;
  initialSeconds: number;
  perMoveSeconds: number;
}[] = [
  { label: `5'+5"`, initialSeconds: 300, perMoveSeconds: 5 },
  { label: `10'+5"`, initialSeconds: 600, perMoveSeconds: 5 },
  { label: `15'+5"`, initialSeconds: 900, perMoveSeconds: 5 },
];
export const MEDIA_TYPE_LABELS: Record<IMediaType, string> = {
  Image: 'Изображение',
  Video: 'Видео',
};
export const CHESS_VARIANT_LABELS: Record<IChessVariant, string> = {
  Standard: 'Стандартная игра',
  '2vs2': '2 на 2',
  Seance: 'Сеанс',
};

export interface IChapter {
  id: string;
  /** Тип главы */
  type: IChapterType;
  /** Имя главы */
  name: string;
  /** Собственное имя родительской главы — тег ParentChapter (без ✦); undefined — корневая */
  parent?: string;
  /** Содержание */
  pgn: Pgn<IPgnMove>;
  /** Положение доски */
  orientation: 'white' | 'black';
  /** Темы (тэги) */
  topics: string[];

  levelMin?: number;
  levelMax?: number;

  chessVariant?: IChessVariant;
  scoringMode?: ITestScoringMode;
  timeControl?: { initialSeconds?: number | null; perMoveSeconds?: number | null };
  media?: IMediaInfo;

  /** Версия */
  ver?: number;
  /** Признак несохранённых изменений */
  dirty?: boolean;
  /** Конфликт сохранения: глава изменена или удалена на сервере другим автором */
  conflict?: IChapterConflict;
  /** Глава лежит слайдом в уроке: какой версией. undefined — не используется */
  usedInLesson?: ILessonUsage;
}

// Конфликтная глава не уходит на сервер, пока автор не выберет «перезаписать»:
// remoteVer — серверная версия, которую перезапись должна заявить; у удаленной
// на сервере главы версии нет — перезапись пересоздает ее новой
export type IChapterConflict = { kind: 'updated'; remoteVer: number } | { kind: 'deleted' };

// Локальный черновик студии: книга целиком в модели IChapter. Правки НЕ уходят на
// сервер до явного «Сохранить» (bulk-запросом). Новые главы живут с временными id
// (tmp_*) и без версии (ver === undefined) — сервер выдаст им настоящие при save.
export type IBookDraft = { version: number; title: string; chapters: IChapter[] };

export type ILevelRange = Pick<IChapter, 'levelMin' | 'levelMax'>;

export interface IMediaInfo {
  type: IMediaType;
  url: string;
}

// Слепок начальной позиции в ChapterMetadataDto.aux — фронтендный контракт,
// сервер хранит его как непрозрачный JSON. По нему превью главы и слайда
// рисуется без разбора PGN. Пишется при каждом сохранении главы (mapMeta);
// поля отсутствуют — стартовая позиция без фишек и стрелок. previewFen
// слайда — фолбэк для снимков, снятых до появления aux
export interface IChapterAux {
  fen?: string;
  tokens?: IToken[];
  arrows?: IColoredArrow[];
  squares?: IColoredSquare[];
}
